// Shared site header for ALL ecosquirrel tools and the landing page.
// One source of truth: editing this file (or the catalog below) changes the header
// everywhere. Per site, only the `tag` prop differs (e.g. "/pwdgen"); the landing
// passes no tag. Lives outside any single app and is imported via the `@shared`
// alias; each app provides the deps (Mantine, react-router, phosphor).
import type { ReactNode } from 'react';
import { Anchor, Button, Container, Group, Menu } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import {
  AsteriskIcon,
  BrowsersIcon,
  CaretDownIcon,
  CodeIcon,
  CoffeeIcon,
  FileCodeIcon,
  FingerprintIcon,
  HashIcon,
  ImageIcon,
  KeyIcon,
  LockKeyIcon,
  PasswordIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  TranslateIcon,
} from '@phosphor-icons/react';

const LOGO = `${import.meta.env.BASE_URL}ecosquirrel.png`;
const S = 16;

type Lang = 'de' | 'en';
type GroupKey = 'security' | 'dev' | 'images';

const TXT: Record<Lang, { switchTo: string } & Record<GroupKey, string>> = {
  de: { switchTo: 'English', security: 'Sicherheit', dev: 'Entwickler-Tools', images: 'Bilder' },
  en: { switchTo: 'Deutsch', security: 'Security', dev: 'Developer tools', images: 'Images' },
};

interface Tool {
  href: string;
  icon: ReactNode;
  de: string;
  en: string;
}

// Central tool catalog — add a new tool here once and it appears in every header.
const GROUPS: { key: GroupKey; tools: Tool[] }[] = [
  {
    key: 'security',
    tools: [
      { href: '/pwdgen/', icon: <PasswordIcon size={S} />, de: 'Passwort-Generator', en: 'Password Generator' },
      { href: '/sshgen/', icon: <KeyIcon size={S} />, de: 'SSH-Key-Generator', en: 'SSH Key Generator' },
      { href: '/pgpgen/', icon: <LockKeyIcon size={S} />, de: 'PGP-Key-Generator', en: 'PGP Key Generator' },
    ],
  },
  {
    key: 'dev',
    tools: [
      { href: '/uuidgen/', icon: <FingerprintIcon size={S} />, de: 'UUID-Generator', en: 'UUID Generator' },
      { href: '/hashgen/', icon: <HashIcon size={S} />, de: 'Hash-Generator', en: 'Hash Generator' },
      { href: '/qrgen/', icon: <QrCodeIcon size={S} />, de: 'QR-Code-Generator', en: 'QR Code Generator' },
      { href: '/regextest/', icon: <AsteriskIcon size={S} />, de: 'Regex-Tester', en: 'Regex Tester' },
      { href: '/base64gen/', icon: <CodeIcon size={S} />, de: 'Base64', en: 'Base64' },
      { href: '/jwtgen/', icon: <ShieldCheckIcon size={S} />, de: 'JWT-Decoder', en: 'JWT Decoder' },
      { href: '/jsongen/', icon: <FileCodeIcon size={S} />, de: 'JSON-Toolkit', en: 'JSON Toolkit' },
    ],
  },
  {
    key: 'images',
    tools: [
      { href: '/favicongen/', icon: <BrowsersIcon size={S} />, de: 'Favicon-Generator', en: 'Favicon Generator' },
      { href: '/imgconv/', icon: <ImageIcon size={S} />, de: 'Bildformat-Converter', en: 'Image Format Converter' },
    ],
  },
];

export function SiteHeader({ tag = '' }: { tag?: string }) {
  const { pathname } = useLocation();
  const lang: Lang = pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'de';
  const txt = TXT[lang];
  const otherHome = lang === 'de' ? '/en' : '/';

  return (
    <header
      style={{
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-card)',
        position: 'sticky',
        top: 0,
        zIndex: 200,
      }}
    >
      <Container size="lg" py="sm">
        <Group justify="space-between">
          {/* Wordmark links to the site root (the landing hub), across apps. */}
          <Anchor
            href="/"
            underline="never"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontFamily: 'Lexend, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'var(--text)',
            }}
          >
            <img src={LOGO} alt="ecosquirrel" height={28} style={{ display: 'block' }} />
            <span>
              ecosquirrel
              {tag && <span style={{ color: 'var(--primary)' }}>{tag}</span>}
            </span>
          </Anchor>

          <Group gap="xs">
            {GROUPS.map((group) => (
              <Menu
                key={group.key}
                shadow="md"
                width={250}
                trigger="click-hover"
                openDelay={80}
                closeDelay={120}
                withinPortal
              >
                <Menu.Target>
                  <Button variant="subtle" size="sm" rightSection={<CaretDownIcon size={14} />}>
                    {txt[group.key]}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {group.tools.map((tool) => (
                    <Menu.Item key={tool.href} component="a" href={tool.href} leftSection={tool.icon}>
                      {lang === 'de' ? tool.de : tool.en}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ))}

            <Button component={Link} to={otherHome} variant="subtle" size="sm" leftSection={<TranslateIcon size={16} />}>
              {txt.switchTo}
            </Button>

            <Button
              component="a"
              href="https://ko-fi.com/S0D120ZG1H"
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              leftSection={<CoffeeIcon size={16} weight="fill" />}
              styles={{ root: { backgroundColor: '#9933ff' } }}
            >
              Buy me a Coffee
            </Button>
          </Group>
        </Group>
      </Container>
    </header>
  );
}
